import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Save, Paperclip, ArrowUp, ArrowDown } from "lucide-react";
import { AUDIENCE } from "@/pages/Training";
import { toast } from "sonner";
import { uploadFile } from "@/lib/upload";

const blank = { title: "", description: "", audience: "tutti", mandatory: false, published: false, pass_score: 80, lessons: [], quiz: [] };

function move(arr, i, d) {
  const a = [...arr]; const j = i + d;
  if (j < 0 || j >= a.length) return a;
  [a[i], a[j]] = [a[j], a[i]]; return a;
}

function LessonEditor({ l, i, courseId, onChange, onRemove, onMove, reload }) {
  const [pct, setPct] = useState(null);
  const upload = async (file) => {
    if (!courseId || !l.id) return toast.error("Salva il corso prima di allegare file");
    try {
      setPct(0);
      const upload_id = await uploadFile(file, setPct);
      await api.post(`/courses/${courseId}/lessons/${l.id}/attachments/from-upload`, { upload_id });
      toast.success("File allegato"); reload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setPct(null); }
  };
  const delAtt = async (aid) => { await api.delete(`/courses/${courseId}/lessons/${l.id}/attachments/${aid}`); reload(); };
  return (
    <div className="border border-slate-200 rounded-xl p-4 space-y-3 bg-slate-50/50" data-testid={`lesson-editor-${i}`}>
      <div className="flex items-center gap-2">
        <span className="font-display font-bold text-sky-700">#{i + 1}</span>
        <Input value={l.title} placeholder="Titolo lezione" onChange={(e) => onChange({ ...l, title: e.target.value })} data-testid={`lesson-title-${i}`} />
        <button type="button" onClick={() => onMove(-1)} className="p-2 text-slate-400 hover:text-slate-700"><ArrowUp className="w-4 h-4" /></button>
        <button type="button" onClick={() => onMove(1)} className="p-2 text-slate-400 hover:text-slate-700"><ArrowDown className="w-4 h-4" /></button>
        <button type="button" onClick={onRemove} className="p-2 text-slate-400 hover:text-rose-600" data-testid={`lesson-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
      </div>
      <Textarea rows={6} value={l.content} placeholder="Contenuto della lezione: procedure, regole, consigli…" onChange={(e) => onChange({ ...l, content: e.target.value })} data-testid={`lesson-content-${i}`} />
      <Input value={l.video_url} placeholder="Link video (YouTube o altro, opzionale)" onChange={(e) => onChange({ ...l, video_url: e.target.value })} data-testid={`lesson-video-${i}`} />
      <div className="flex flex-wrap items-center gap-2">
        {(l.attachments || []).map((a) => (
          <span key={a.id} className="inline-flex items-center gap-1 text-xs bg-white border border-slate-200 rounded-full px-3 py-1">
            <Paperclip className="w-3 h-3" /> {a.filename}
            <button type="button" onClick={() => delAtt(a.id)} className="text-rose-500 ml-1">×</button>
          </span>
        ))}
        <label className="text-xs font-semibold text-sky-700 cursor-pointer inline-flex items-center gap-1">
          <Paperclip className="w-3.5 h-3.5" /> {pct !== null ? `Caricamento ${pct}%` : "Allega PDF, immagine o video"}
          <input type="file" accept="application/pdf,image/*,video/mp4,video/quicktime,video/webm" className="hidden" disabled={pct !== null} onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid={`lesson-attach-${i}`} />
        </label>
      </div>
    </div>
  );
}

function QuestionEditor({ q, i, onChange, onRemove }) {
  const setOpt = (k, v) => { const o = [...q.options]; o[k] = v; onChange({ ...q, options: o }); };
  return (
    <div className="border border-slate-200 rounded-xl p-4 space-y-2" data-testid={`question-editor-${i}`}>
      <div className="flex gap-2">
        <Input value={q.question} placeholder={`Domanda ${i + 1}`} onChange={(e) => onChange({ ...q, question: e.target.value })} data-testid={`question-text-${i}`} />
        <button type="button" onClick={onRemove} className="p-2 text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
      </div>
      {q.options.map((o, k) => (
        <label key={k} className="flex items-center gap-2 text-sm">
          <input type="radio" name={`correct-${i}`} checked={q.correct === k} onChange={() => onChange({ ...q, correct: k })} data-testid={`question-${i}-correct-${k}`} />
          <Input value={o} placeholder={`Risposta ${k + 1}`} onChange={(e) => setOpt(k, e.target.value)} data-testid={`question-${i}-option-${k}`} />
        </label>
      ))}
      <p className="text-xs text-slate-500">Seleziona il pallino della risposta giusta.</p>
    </div>
  );
}

export default function CourseEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "nuovo";
  const [c, setC] = useState(blank);
  const load = () => api.get(`/courses/${id}`).then((r) => setC(r.data));
  const reloadAttachments = () => api.get(`/courses/${id}`).then((r) => {
    const byId = Object.fromEntries(r.data.lessons.map((l) => [l.id, l.attachments]));
    setC((p) => ({ ...p, lessons: p.lessons.map((l) => (l.id && byId[l.id] ? { ...l, attachments: byId[l.id] } : l)) }));
  });
  useEffect(() => { if (!isNew) { load(); } }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch) => setC((p) => ({ ...p, ...patch }));
  const save = async () => {
    try {
      const { data } = isNew ? await api.post("/courses", c) : await api.put(`/courses/${id}`, c);
      toast.success("Corso salvato");
      if (isNew) navigate(`/formazione/${data.id}/modifica`, { replace: true }); else setC(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async () => {
    if (!window.confirm("Eliminare il corso e tutti i progressi?")) return;
    await api.delete(`/courses/${id}`); navigate("/formazione");
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="course-editor">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="font-display text-3xl font-extrabold text-slate-900">{isNew ? "Nuovo corso" : "Modifica corso"}</h1>
        <div className="flex gap-2">
          {!isNew && <Button variant="outline" onClick={remove} data-testid="course-delete-button"><Trash2 className="w-4 h-4 mr-2" /> Elimina</Button>}
          <Button onClick={save} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="course-save-button"><Save className="w-4 h-4 mr-2" /> Salva</Button>
        </div>
      </header>
      <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <div><Label>Titolo</Label><Input value={c.title} onChange={(e) => set({ title: e.target.value })} data-testid="course-title-input" /></div>
        <div><Label>Descrizione</Label><Textarea rows={3} value={c.description} onChange={(e) => set({ description: e.target.value })} data-testid="course-description-input" /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Destinatari</Label>
            <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" value={c.audience} onChange={(e) => set({ audience: e.target.value })} data-testid="course-audience-select">
              {Object.entries(AUDIENCE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></div>
          <div><Label>Punteggio minimo per superare (%)</Label><Input type="number" min="1" max="100" value={c.pass_score} onChange={(e) => set({ pass_score: e.target.value })} data-testid="course-pass-score-input" /></div>
        </div>
        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2 text-sm"><Switch checked={c.mandatory} onCheckedChange={(v) => set({ mandatory: v })} data-testid="course-mandatory-switch" /> Obbligatorio</label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={c.published} onCheckedChange={(v) => set({ published: v })} data-testid="course-published-switch" /> Pubblicato (i dipendenti lo vedono e ricevono l'avviso)</label>
        </div>
      </section>
      <section className="space-y-3">
        <h2 className="font-display text-xl font-bold text-slate-900">Lezioni</h2>
        {c.lessons.map((l, i) => (
          <LessonEditor key={l.id || i} l={l} i={i} courseId={isNew ? null : id} reload={reloadAttachments}
            onChange={(nl) => set({ lessons: c.lessons.map((x, k) => (k === i ? nl : x)) })}
            onRemove={() => set({ lessons: c.lessons.filter((_, k) => k !== i) })}
            onMove={(d) => set({ lessons: move(c.lessons, i, d) })} />
        ))}
        <Button variant="outline" onClick={() => set({ lessons: [...c.lessons, { title: "", content: "", video_url: "", attachments: [] }] })} data-testid="add-lesson-button"><Plus className="w-4 h-4 mr-2" /> Aggiungi lezione</Button>
      </section>
      <section className="space-y-3">
        <h2 className="font-display text-xl font-bold text-slate-900">Quiz finale</h2>
        {c.quiz.map((q, i) => (
          <QuestionEditor key={q.id || i} q={q} i={i}
            onChange={(nq) => set({ quiz: c.quiz.map((x, k) => (k === i ? nq : x)) })}
            onRemove={() => set({ quiz: c.quiz.filter((_, k) => k !== i) })} />
        ))}
        <Button variant="outline" onClick={() => set({ quiz: [...c.quiz, { question: "", options: ["", "", ""], correct: 0 }] })} data-testid="add-question-button"><Plus className="w-4 h-4 mr-2" /> Aggiungi domanda</Button>
      </section>
    </div>
  );
}
